import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Header as HeaderSuite, Avatar, SelectPicker } from 'rsuite'
import MenuIcon from '@rsuite/icons/Menu'
import PlusIcon from '@rsuite/icons/Plus'

import { Button } from 'shared/atoms'
import { useAuth } from 'contexts/AuthContext'
import {
  useSelectedProjectId,
  useSetSelectedProjectId
} from 'api/projectSelectionApi'
import { useGetProjectsQuery } from 'pages/projects/projectApiSlice'
import { getMenuItemByPath } from 'config/MenuItems'
import { getDashboardPath } from 'utils'
import tenantConfig from 'config/tenant'
import './header.scss'

// Lazily loaded, for two reasons: it keeps the project form out of the bundle
// every page pays for, and it breaks the import cycle this would otherwise
// create (Header → CreateEditProject → `shared` barrel → Header).
const CreateEditProject = React.lazy(
  () => import('pages/projects/projectCreateEdit/CreateEditProject')
)

interface HeaderProps {
  onMenuToggle?: () => void
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const location = useLocation()
  const { role, user, isLoggedIn, hasPermission } = useAuth()
  const canCreateProject = hasPermission('project.create')
  // Whether the "＋ Add project" drawer (opened from the picker's footer) is up.
  const [addOpen, setAddOpen] = useState(false)
  // Project selection lives in the api/projectSelectionApi slice. Header
  // is the canonical reader/writer; other call sites pull from the same
  // slice (currently bridged through `useAuth()` for back-compat).
  const selectedProjectId = useSelectedProjectId()
  const setProjectId = useSetSelectedProjectId()

  // Only fetch projects once the user is logged in; the picker is hidden when
  // there's nothing to pick (auth screens, no projects in the tenant, etc.).
  const { data: projectsData } = useGetProjectsQuery(
    { page: { size: 100, number: 0 }, filters: [] },
    { skip: !isLoggedIn }
  )
  const projectOptions = useMemo(
    () =>
      (projectsData?.content ?? []).map((p: any) => ({
        value: p.id,
        label: p.name || p.code || 'Untitled project'
      })),
    [projectsData]
  )

  // Validate the persisted project selection against the actual project
  // list the user has access to, then auto-pick the first one if none is
  // chosen yet. Runs at the header level so every page benefits without
  // having to implement its own logic.
  //
  // Why this matters: `selectedProjectId` lives in localStorage so it
  // survives reloads. But on a shared machine — user A picks project X
  // → logs out → user B logs in without any projects — localStorage
  // would still hold X and every downstream API call would silently use
  // user B's stale-and-not-actually-theirs project ID. Clearing it the
  // moment we detect the mismatch makes the FE refuse to fabricate
  // a projectId out of thin air for users with no project access.
  useEffect(() => {
    if (!isLoggedIn) return
    // Wait for the list to actually load before validating — undefined
    // means the query is still in flight; falsely clearing here would
    // bounce a perfectly valid selection.
    if (!projectsData) return

    const projectIds = (projectsData.content ?? []).map((p: any) => p.id)

    // Persisted projectId is no longer valid for this user (different
    // user / project deleted / access revoked) — drop it so downstream
    // API calls don't run with a stale ID.
    if (selectedProjectId && !projectIds.includes(selectedProjectId)) {
      setProjectId(null)
      return
    }

    // Nothing selected yet — auto-pick the first available project so
    // the user lands on usable data without an extra click. No-op when
    // the user has zero projects.
    if (!selectedProjectId && projectIds.length > 0) {
      setProjectId(projectIds[0])
    }
  }, [isLoggedIn, selectedProjectId, projectsData, setProjectId])

  // Always render the picker once the user is logged in — even with no
  // projects yet — so users can see and use the global selector. When the
  // list is empty rsuite shows the placeholder and a "No data" hint.
  const showProjectPicker = isLoggedIn

  const menuItem = getMenuItemByPath(location.pathname, role)

  // Detail routes (`/<section>/<id>`) show "<Entity> Details" in the header.
  const DETAIL_TITLES: Record<string, string> = {
    projects: 'Project Details',
    plots: 'Plot Details',
    leads: 'Lead Details',
    quotes: 'Quote Details',
    bookings: 'Booking Details',
    payments: 'Payment Details',
    customers: 'Customer Details',
    tasks: 'Task Details',
    brokers: 'Broker Details'
  }
  const segments = location.pathname.split('/').filter(Boolean)
  const detailTitle =
    segments.length === 2 ? DETAIL_TITLES[segments[0]] : undefined
  const pageTitle = detailTitle ?? menuItem?.name ?? 'Dashboard'

  const displayName = user?.displayName || user?.username || 'User'
  const roleLabel = role
    ? `${role.replace('ROLE_', '').slice(0, 1)}${role.replace('ROLE_', '').slice(1).toLowerCase()}`
    : ''
  const initials = displayName.charAt(0).toUpperCase()

  return (
    <>
      <Link to={getDashboardPath()} className="header__logo-fixed">
        <img
          src={tenantConfig.logoIcon}
          className="header__logo-img"
          alt={tenantConfig.name}
        />
      </Link>
      <HeaderSuite className="header">
        <div className="header__content">
          {onMenuToggle && (
            <button
              type="button"
              className="header__menu-toggle"
              onClick={onMenuToggle}
              aria-label="Toggle menu"
            >
              <MenuIcon />
            </button>
          )}
          <div className="header__main">
            <h1 className="header__title">{pageTitle}</h1>
            <div className="header__right">
              {showProjectPicker && (
                <SelectPicker
                  data={projectOptions}
                  value={selectedProjectId}
                  onChange={(v) => setProjectId(v ?? null)}
                  placeholder="All projects"
                  searchable
                  cleanable={false}
                  size="md"
                  className="header__project-picker"
                  container={() => document.body}
                  menuClassName="header__project-menu"
                />
              )}
              {/* Creating a project is a global action now — it sits beside
                  the project picker rather than on the Projects page toolbar,
                  so it's reachable from anywhere in the app. */}
              {showProjectPicker && canCreateProject && (
                <Button
                  appearance="primary"
                  className="header__project-add"
                  onClick={() => setAddOpen(true)}
                  title="Create a new project"
                >
                  <PlusIcon />
                  <span className="header__project-add-label">New Project</span>
                </Button>
              )}
              {/* User identity — a read-only chip. My profile and Sign out
                  live in the sidebar, so neither is duplicated here. */}
              <div className="header__user-toggle header__user-toggle--static">
                <Avatar circle size="sm" className="header__avatar">
                  {initials}
                </Avatar>
                {/* `title` so a name clipped by the chip's max-width is
                    still readable on hover. */}
                <div className="header__user-info">
                  <span className="header__user-name" title={displayName}>
                    {displayName}
                  </span>
                  <span className="header__user-role" title={roleLabel}>
                    {roleLabel}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeaderSuite>

      {/* Mounted only once opened, so the lazy chunk isn't fetched until the
          user actually asks to add a project. Selecting the new project on
          create means the page underneath switches to it immediately. */}
      {addOpen && (
        <Suspense fallback={null}>
          <CreateEditProject
            open={addOpen}
            onClose={() => setAddOpen(false)}
            isEditMode={false}
            onCreated={(project) => setProjectId(project.id)}
          />
        </Suspense>
      )}
    </>
  )
}

export default Header
